import React, { useCallback, useEffect, useState } from 'react';
import { View, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import moment from 'moment';
import MyDataTable from '../../../components/MyDataTable';
import MyAppText from '../../../components/MyAppText';
import Toast from 'react-native-toast-message';
import { dispatchService } from '../../../services/central-api/dispatch';
import { RouteProp, useFocusEffect, useIsFocused, useRoute } from '@react-navigation/native';
import { DispatchParamList } from '../../../typings/Params';
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import 'react-calendar/dist/Calendar.css';
import '../../../styles/date-ranger-picker.css';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import useFilters from '../../../components/Filter/hooks/useFilters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import Filter from './Filters';
import ReportModal from './ReportModal';
import getBestContrastColor from '../../../services/best-contrast-color';
import { useAuth } from '../../../context/Auth';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import { RootNavigation } from '../../../Routes';
import { openExternalLinkInNewTab } from '../../../services/navigation-assistant';
import Badge from '../../../components/Badge';

export default function Reports({ navigation }: { navigation: RootNavigation; }) {
    const { styles } = useStyles(styleSheet);
    const route = useRoute<RouteProp<DispatchParamList, 'DispatchReports'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<DispatchParamList['DispatchReports']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const [isDownloadingIds, setIsDownloadingIds] = useState<number[]>([]);
    const [reports, setReports] = useState<ReportOccurrence[]>([]);
    const [selectedReport, setSelectedReport] = useState<ReportOccurrence>();
    const [totalItems, setTotalItems] = useState(0);

    function handleFormClose() {
        refetch();
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            refetch();
        }
    }, [isFocused, navigation, filters]));

    function canDownloadReport(row?: ReportOccurrence) {
        if (!row) {
            return false;
        }

        return (
            authenticatedUser?.isAdmin
            || authenticatedUser?.permissions.dispatch_module_access
            || authenticatedUser?.permissions.dispatch_download_report
            || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_download_report
        ) && row.status === 'success' && !isDownloadingIds.length;
    }

    const queryResult = useQuery({
        queryKey: ['occurrenceReports', filters],
        queryFn: () => dispatchService.getReports(filters),
        enabled: !!authenticatedUser && isFocused,
        refetchInterval: isFocused ? 15000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isInitialLoading;
    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setReports(data.rows);
        setTotalItems(data.count);
    }, [data]);

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    async function downloadReport() {
        if (!selectedReport) {
            return;
        }

        if (!canDownloadReport(selectedReport)) {
            return;
        }

        try {
            setIsDownloadingIds([
                ...isDownloadingIds,
                selectedReport.id
            ]);
            const video = await dispatchService.getReport(selectedReport.id);
            openExternalLinkInNewTab(video.resultUrl);
        } catch (error) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsDownloadingIds(isDownloadingIds.filter(id => id != selectedReport.id));
        }
    }

    function getStatus(status: 'success' | 'failed' | 'pending') {
        if (status === 'success') {
            return <Badge mode='SQUARE' color='green'>{translate('available')}</Badge>;
        } else if (status === 'pending') {
            return <Badge mode='SQUARE' color='orange'>{translate('processing')}</Badge>;
        }
        return <Badge mode='SQUARE' color='red'>{translate('fail')}</Badge>;
    }

    return (
        <Container>
            <Header
                title={translate('reports')}
                actions={<> {
                    (
                        authenticatedUser?.isAdmin
                        || authenticatedUser?.permissions.dispatch_module_access
                        || authenticatedUser?.permissions.dispatch_generate_report
                        || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_reports
                    ) &&
                    <ReportModal
                        onClose={() => setSelectedReport(undefined)}
                        onApplyFilters={handleFormClose}
                        report={selectedReport}
                        canDownloadReport={canDownloadReport(selectedReport)}
                        downloadReport={downloadReport}
                    />
                }</>}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredReports')}
                    zIndex={2}
                    filter={<Filter
                        filters={filters}
                        setFilters={setFilters}
                        textFilter={textFilter}
                        setTextFilter={setTextFilter}
                    />}
                    filterInput={<FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('status'),
                                cell: row => <View>
                                    {getStatus(row.status)}
                                </View>,
                                wrap: true,
                                grow: 0
                            },
                            {
                                name: translate('name'),
                                selector: row => row.name,
                                wrap: true
                            },
                            {
                                name: translate('user'),
                                selector: row => row.user?.name || '',
                                wrap: true
                            },
                            {
                                name: 'Token',
                                selector: row => row.token,
                                wrap: true
                            },
                            {
                                name: translate('createdAt'),
                                selector: row => moment(row.createdAt).format('DD/MM/YYYY - HH:mm'),
                                wrap: true
                            },
                            {
                                name: translate('filters'),
                                cell: row => [
                                    row.actingBodyId ? 1 : 0,
                                    row.filters.assumedBy ? 1 : 0,
                                    row.filters.receivedBy ? 1 : 0,
                                    row.filters.transferredBy ? 1 : 0,
                                    row.filters.finishedBy ? 1 : 0,
                                    (row.filters.actingBodyCommands?.length ?? 0) > 0 ? 1 : 0,
                                    (row.filters.triggerTypes?.length ?? 0) > 0 ? 1 : 0,
                                    (row.filters.occurrenceTypes?.length ?? 0) > 0 ? 1 : 0,
                                    (row.filters.occurrenceTags?.length ?? 0) > 0 ? 1 : 0,
                                    (row.filters.unitNames?.length ?? 0) > 0 ? 1 : 0,
                                    row.filters.priority && row.filters.priority !== 'all' ? 1 : 0,
                                    row.filters.situation && row.filters.situation !== 'all' ? 1 : 0,
                                    (row.filters.attachmentTypes?.length ?? 0) > 0 ? 1 : 0,
                                    row.filters.policeReport ? 1 : 0,
                                    (row.filters.actingBodyUnits?.length ?? 0) > 0 ? 1 : 0,
                                    (row.filters.displaceReasons?.length ?? 0) > 0 ? 1 : 0,
                                    (row.filters.mapPolygons?.length ?? 0) > 0 ? 1 : 0,
                                    (row.filters.latitude && row.filters.longitude && row.filters.radius) ? 1 : 0,
                                    (row.filters.withDispatch || row.filters.withoutDispatch) ? 1 : 0,
                                    (row.filters.withOccurrenceDocument || row.filters.withoutOccurrenceDocument) ? 1 : 0,
                                    (row.filters.cameraTagsNames?.length ?? 0) > 0 ? 1 : 0,
                                    (row.filters.finishReasonCategories?.length ?? 0) > 0 ? 1 : 0,
                                    (row.filters.finishReasonSubCategories?.length ?? 0) > 0 ? 1 : 0,
                                    (row.filters.involvedPersonSearch || row.filters.handcuffUsed || row.filters.arrested) ? 1 : 0,
                                ].reduce((a, b) => a + b, 0),
                                wrap: true,
                                grow: 0
                            },
                            {
                                name: translate('tags'),
                                cell: row =>
                                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', width: '100%', gap: 5, paddingVertical: 5 }}>
                                        {row.tags ?
                                            row.tags.map((tag) =>
                                                <View key={tag.id} style={[styles.tagsMarker, { backgroundColor: tag.color }]}>
                                                    <MyAppText style={styles.markerText(tag.color)}>
                                                        {tag.name}
                                                    </MyAppText>
                                                </View>
                                            ) : null
                                        }
                                    </View>
                            },
                            {
                                button: true,
                                cell: row => {
                                    const actions: Action[] = [];

                                    if (canDownloadReport(row)) {
                                        actions.push({
                                            content: translate('downloadReport'),
                                            onPress: () => {
                                                setSelectedReport(row);
                                                downloadReport();
                                            }
                                        });
                                    }

                                    actions.push({
                                        content: translate('viewFilters'),
                                        onPress: () => {
                                            setSelectedReport(row);
                                        }
                                    });
                                    return (
                                        <DropdownButton
                                            actions={actions}
                                            isLoading={isDownloadingIds.includes(row.id)}
                                        />
                                    );
                                }
                            }
                        ]}
                        data={reports}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={totalItems}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    statusStyle: (status: VideoAnalysisStatus) => {
        return {
            color: status === 'success' ? theme.colors.successes['500'] : theme.colors.errors['500']
        };
    },
    tagsMarker: {
        borderRadius: 8,
        minHeight: 16,
        paddingLeft: 10,
        paddingRight: 10,
        maxWidth: '100%'
    },
    markerText: (tagColor: string) => ({
        fontWeight: '500',
        color: getBestContrastColor(tagColor),
        fontSize: 11
    }),
}));
